import { useRef, useState } from 'react';
import { UploadCloud, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import api from '../../src/api';

type UploadState = 'VALIDATING' | 'READY' | 'UPLOADING' | 'SUCCESS' | 'ERROR';

interface MagicLinkUploaderProps {
  token: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export function MagicLinkUploader({ token }: MagicLinkUploaderProps) {
  const [state, setState] = useState<UploadState>('READY');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const validateFile = (file: File): string | null => {
    if (!file.name.toLowerCase().endsWith('.zip')) {
      return 'Solo se permiten archivos ZIP.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'El archivo no debe exceder 5MB.';
    }
    return null;
  };

  const onFileSelected = (file: File | null) => {
    if (!file) return;
    setState('VALIDATING');
    const validationError = validateFile(file);
    if (validationError) {
      setState('ERROR');
      setErrorMessage(validationError);
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
    setErrorMessage('');
    setState('READY');
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setState('UPLOADING');
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('archivo', selectedFile);
      const response = await api.post(`/speaker/upload/${encodeURIComponent(token)}`, formData);
      setSuccessMessage(response.data?.message || 'Material recibido. Tu credencial será enviada en breve.');
      setState('SUCCESS');
    } catch (error: any) {
      setState('ERROR');
      setErrorMessage(error?.response?.data?.message || 'No fue posible subir el material.');
    }
  };

  return (
    <Card className="border-t-4 border-t-[#c9a227]">
      <CardHeader>
        <CardTitle className="text-[#0f2744]">Carga de Material Final</CardTitle>
        <CardDescription>
          Formato permitido: <b>.zip</b> (máximo 5MB)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          className="border-2 border-dashed border-[#0f2744]/25 rounded-lg p-6 text-center bg-gray-50"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files?.[0] || null;
            onFileSelected(file);
          }}
        >
          <UploadCloud className="mx-auto h-8 w-8 text-[#0f2744]" />
          <p className="mt-2 text-sm text-gray-600">Arrastra tu ZIP aquí o selecciónalo manualmente.</p>
          <Input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            className="mt-4"
            onChange={(e) => onFileSelected(e.target.files?.[0] || null)}
          />
        </div>

        {selectedFile && (
          <p className="text-xs text-gray-700">
            Archivo: <span className="font-semibold">{selectedFile.name}</span>
          </p>
        )}

        {state === 'ERROR' && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {errorMessage}
          </div>
        )}

        {state === 'SUCCESS' && (
          <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            {successMessage || 'Material recibido. Tu credencial será enviada en breve.'}
          </div>
        )}

        <Button
          className="w-full bg-[#0f2744] hover:bg-[#1c3d63] text-white"
          onClick={handleUpload}
          disabled={!selectedFile || state === 'UPLOADING' || state === 'SUCCESS'}
        >
          {state === 'UPLOADING' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Subir material
        </Button>
      </CardContent>
    </Card>
  );
}
